/**
 * topUpDocumentLayout.ts — silnik formatek dokumentów (E4, decyzja 4.1.d).
 *
 * Czysta funkcja transformu layoutu dokumentu, stosowana przy renderze do
 * EFEKTYWNEGO configu (active lub override sesyjny) tuż przed
 * <RecursiveFormRenderer>. Biegnie ZAWSZE (bezwarunkowo — `sourceViewSlug`
 * jest wyłącznie informacyjny, nie bramkuje transformu) i jest IDEMPOTENTNA:
 * każdy dodatek jest gated „jeśli brak" (dedup po slugu zakładki / typie
 * elementu), a istniejące `id`/`children` są zachowywane, nie regenerowane.
 *
 * Gwarancje wyniku:
 *  - zawsze znormalizowany kształt `{ elements: [{ type: 'tabs', tabs }] }`
 *    (domyka lukę flat-shape: płaski layout centralny bez wrappera `tabs`
 *    fałszywie przechodził `isAlreadyNewShape` i renderował sekcje poza
 *    zakładkami);
 *  - zakładka-target `data` istnieje (znaleziona po slugu, wyłoniona jako
 *    pierwsza zakładka z polami, albo utworzona/zsyntetyzowana);
 *  - dla configu null/pustego zakładka `data` jest syntetyzowana z
 *    `ctx.coreFieldSlugs`, OPAKOWANYMI w JEDEN element `section`
 *    (`settings.columns: 2`) — `SectionRenderer` jest jedynym elementem
 *    `RecursiveFormRenderer`, który daje grid kolumn; bez wrappera pola-liście
 *    stackowałyby się jedną kolumną (regresja vs usunięty `BareDocDataTab`);
 *  - top-up NIE dokleja żadnego bloku ani listy pól dodatkowych: pole dodatkowe
 *    pokazuje się wyłącznie tam, gdzie ktoś postawił je w kreatorze układu jako
 *    zwykły liść `field` (rysuje go wspólny silnik przez `renderField`);
 *    istniejących elementów zakładek top-up nie modyfikuje — stary blok
 *    `ext_fields_view` zapisany w układzie przechodzi nietknięty (nie ma
 *    renderera, więc `RecursiveFormRenderer` rysuje go jako nic);
 *  - zakładki strony z `ctx.pageTabs` dosypane z dedupem po slugu; kolizja
 *    (np. centralny layout ma już `audit_log`) = istniejąca wygrywa,
 *    `tabRenderers[slug]` strony i tak nadpisze jej treść;
 *  - wstrzykiwane zakładki NIE dostają `visibleRoleIds` (puste = widoczne
 *    dla wszystkich ról);
 *  - zakładki posortowane po `sortOrder`.
 *
 * NIE dotyka `migrate-layout-to-recursive.ts` (kopia 1:1 z API) — top-up
 * żyje osobno.
 */

import type { LayoutConfig, LayoutElement, TabItem } from '@/components/form/RecursiveFormRenderer';

export interface PageTabSpec {
  slug: string; // literalny: 'data' | 'audit_log' | 'additional' (+ własne, np. 'parties')
  title: string; // i18n (t(...) rozwiązane przez stronę)
  /** typ placeholdera-dziecka dla zakładek non-data. Dla 'data' pomijany. */
  placeholderType?: 'audit_log_view' | 'panel_view';
  sortOrder: number;
}

export interface TopUpContext {
  /** stałe zakładki strony do zagwarantowania (kolejność = sortOrder). */
  pageTabs: PageTabSpec[];
  /** slugi core-pól w kolejności seeda — użyte TYLKO gdy config==null/pusty (synteza data taba). */
  coreFieldSlugs: string[];
  uuid?: () => string; // testowalność; default crypto.randomUUID
}

/** Czy tab zawiera bezpośrednio sekcję lub pole (= „zakładka z polami"). */
function hasFieldBearingChildren(tab: TabItem): boolean {
  return tab.children.some((el) => el.type === 'section' || el.type === 'field');
}

/** Zwraca ZAWSZE znormalizowany { elements:[{ type:'tabs', tabs:[...] }] }. */
export function topUpDocumentLayout(config: LayoutConfig | null, ctx: TopUpContext): LayoutConfig {
  const uuid = ctx.uuid ?? (() => crypto.randomUUID());
  const dataSpec = ctx.pageTabs.find((spec) => spec.slug === 'data');
  const dataTitle = dataSpec?.title ?? 'data';

  // ── Krok 1: normalizacja do listy TabItem ─────────────────────────────────
  // Istniejący wrapper 'tabs' zachowuje tożsamość (id/settings) — idempotencja.
  let existingTabsElement: LayoutElement | null = null;
  let tabs: TabItem[];

  const rootElements = config?.elements ?? [];
  if (rootElements.length === 0) {
    tabs = []; // config null / elements puste → synteza w kroku 3
  } else {
    const tabsElement = rootElements.find((el) => el.type === 'tabs');
    if (tabsElement) {
      existingTabsElement = tabsElement;
      tabs = [...(tabsElement.tabs ?? [])];
    } else {
      // Luka flat-shape (`isAlreadyNewShape` false-positive): płaskie
      // sekcje/pola bez wrappera — owiń WSZYSTKIE root-elementy w jeden tab data.
      tabs = [
        {
          id: uuid(),
          slug: 'data',
          title: dataTitle,
          sortOrder: 0,
          children: rootElements,
        },
      ];
    }
  }
  const wasEmpty = tabs.length === 0;

  // ── Krok 2: wyznacz target „data" ────────────────────────────────────────
  let dataTarget =
    tabs.find((tab) => tab.slug === 'data') ?? tabs.find((tab) => hasFieldBearingChildren(tab));
  if (!dataTarget) {
    dataTarget = {
      id: uuid(),
      slug: 'data',
      title: dataTitle,
      sortOrder: dataSpec?.sortOrder ?? 0,
      children: [],
    };
    tabs.push(dataTarget);
  }

  // ── Krok 3: synteza pól core gdy config był null/pusty ───────────────────
  // Finding 1: pola-liście są OPAKOWANE w jeden element `section` (2 kolumny) —
  // `SectionRenderer` (RecursiveFormRenderer.tsx) jest jedynym elementem, który
  // daje `grid md:grid-cols-2`; bez wrappera pola stackowałyby się 1-kolumnowo
  // (regresja vs usunięty `BareDocDataTab`, który miał własny 2-kol. grid).
  if (wasEmpty) {
    const fieldsSection: LayoutElement = {
      id: uuid(),
      type: 'section',
      settings: { columns: 2 },
      children: ctx.coreFieldSlugs.map(
        (slug): LayoutElement => ({ id: uuid(), type: 'field', fieldId: slug }),
      ),
    };
    const synthesized: TabItem = {
      ...dataTarget,
      children: [fieldsSection],
    };
    tabs = tabs.map((tab) => (tab === dataTarget ? synthesized : tab));
  }

  // ── Krok 5: dosypka zakładek strony (dedup po slugu) ─────────────────────
  // 'data' jest zawsze zaspokojona przez target z kroków 1-3 — także gdy
  // targetem została zakładka o WŁASNYM slugu (tabbed-bez-data); dosypanie
  // wtedy drugiej, pustej 'data' łamałoby idempotencję.
  const specs = [...ctx.pageTabs].sort((a, b) => a.sortOrder - b.sortOrder);
  for (const spec of specs) {
    if (spec.slug === 'data') continue;
    if (tabs.some((tab) => tab.slug === spec.slug)) continue; // istniejąca wygrywa
    tabs.push({
      id: uuid(),
      slug: spec.slug,
      title: spec.title,
      sortOrder: spec.sortOrder,
      children: spec.placeholderType
        ? [{ id: uuid(), type: spec.placeholderType, settings: {} }]
        : [],
    });
  }

  // ── Krok 6: zwrot — zawsze znormalizowany, posortowany po sortOrder ──────
  const sorted = [...tabs].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const wrapper: LayoutElement = existingTabsElement
    ? { ...existingTabsElement, tabs: sorted }
    : { id: uuid(), type: 'tabs', tabs: sorted };
  return { elements: [wrapper] };
}
